import { TYPE } from "vue-toastification";
import { apiCall, apiCallParams, getTenantId, logResponse } from "@/core/api";
import { showAlert } from "@/core/popup";
import i18n from "@/core/i18n/i18n";

const { t } = i18n.global;

export async function getOrganizationList(): Promise<any> {
  const resource = "/organizations";
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getOrganization(organizationId: string): Promise<any> {
  const endPoint = `/organizations/${organizationId}`;
  const response = await apiCall("GET", endPoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function deleteOrganization(organizationId: string): Promise<any> {
  const endPoint = `/organizations/${organizationId}`;
  const response = await apiCall("DELETE", endPoint);
  if (
    response.code === 403 &&
    response.message === t("Organization.LOGGED_IN_ORG_ERROR")
  ) {
    showAlert(TYPE.ERROR, t("Organization.LOGGED_IN_ORG_ERROR"));
  } else if (
    response.code === 403 &&
    response.message === t("Organization.SECURITY_VIOLATION")
  ) {
    showAlert(TYPE.ERROR, t("Organization.SECURITY_VIOLATION"));
  } else if (
    response.code === 403 &&
    response.message === t("Organization.LAST_ORG_ERROR")
  ) {
    showAlert(TYPE.ERROR, t("Organization.LAST_ORG_ERROR"));
  } else if (
    response.code === 403 &&
    response.message === t("Organization.ACTIVE_FLOWS_ERROR")
  ) {
    showAlert(TYPE.ERROR, t("Organization.ACTIVE_FLOWS_ERROR"));
  } else if (
    response.code === 403 &&
    response.message === t("Organization.AUTHENTICATION_ERROR")
  ) {
    showAlert(TYPE.ERROR, t("Organization.AUTHENTICATION_ERROR"));
  } else {
    logResponse(response);
  }
  return response;
}

export async function addOrganization(organization: any): Promise<any> {
  const endPoint = "/organizations/create";
  const response = await apiCall("POST", endPoint, organization);
  logResponse(response);
  return response;
}

export async function editOrganization(organization: any): Promise<any> {
  const endPoint = "/organizations/update";
  const response = await apiCall("PUT", endPoint, organization);
  logResponse(response);
  return response;
}

export async function inlineEditOrganization(organization: any): Promise<any> {
  const endPoint = "/organizations/inline-edit";
  const response = await apiCall("POST", endPoint, organization);
  logResponse(response);
  return response;
}

export async function checkConnections(organization: any): Promise<any> {
  const endPoint = "/organizations/check";
  const response = await apiCall("PUT", endPoint, organization);
  if (response.code === 200) {
    return response.message;
  }
  logResponse(response);
  return undefined;
}

export async function getOrganizationListing(params: any): Promise<any> {
  const endpoint = "/organizations/list";
  const response = await apiCallParams("GET", endpoint, params);
  if (response.code === 200) {
    return response.page;
  }
  logResponse(response);
  return undefined;
}

export async function deleteWorkspace(
  organizationId: string,
  tenantName: string
): Promise<any> {
  const endPoint = `/data-store-plus/workspace/${organizationId}/${tenantName}`;
  const response = await apiCall("DELETE", endPoint);
  logResponse(response);
  return response;
}

export async function isWorkspaceCreated(
  organizationId: string,
  tenantName: string
): Promise<boolean> {
  const endPoint = `/data-store-plus/workspace/created/${organizationId}/${tenantName}`;
  const response = await apiCall("GET", endPoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return false;
}

/**
 * OT-4982 §14: the effective value of an organization-scoped config. The gateway returns the
 * organization's own row, else walks up to the tenant row, else the environment. Returns null when
 * nothing is found (HTTP 200 with an empty result) and throws on any other response, so a failed
 * read is never mistaken for "not set" — unlike getConfiguration, which returns undefined for both.
 */
export async function getOrganizationConfig(
  key: string,
  organizationId: string
): Promise<any> {
  const endPoint = `/configs/${key}/ORGANIZATION/${organizationId}`;
  const response = await apiCall("GET", endPoint);
  if (response.code === 200) {
    if (Array.isArray(response.result) && response.result.length > 0) {
      return response.result[0];
    }
    return null;
  }
  logResponse(response);
  throw new Error(
    `Failed to load organization config '${key}' (code ${response.code}).`
  );
}

/**
 * OT-4982: the current tenant's own value of a config, the ceiling for an organization's value. Same
 * contract as getOrganizationConfig: the tenant row, else the gateway's environment entry (a DTO with
 * no value when the key is not there), null on an empty result, and a throw on any other response.
 */
export async function getTenantLevelConfig(key: string): Promise<any> {
  const endPoint = `/configs/${key}/TENANT/${getTenantId()}`;
  const response = await apiCall("GET", endPoint);
  if (response.code === 200) {
    if (Array.isArray(response.result) && response.result.length > 0) {
      return response.result[0];
    }
    return null;
  }
  logResponse(response);
  throw new Error(
    `Failed to load tenant config '${key}' (code ${response.code}).`
  );
}

/**
 * OT-4982 §15: writes an organization-level config row. Returns the response; the caller checks
 * its code.
 */
export async function updateOrganizationConfig(config: any): Promise<any> {
  const response = await apiCall("PUT", "/configs", config);
  logResponse(response);
  return response;
}
